import { FilterSegments, PageHeader, PageTitle } from '@westhetics/ui'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import {
  OPERATIONS_PERIODS,
  OPERATION_AREA_FILTERS,
  useOperationsOverview,
  type OperationArea,
  type OperationAreaFilter,
  type OperationsPeriod,
} from './api'
import { AutentiqueEventsSection } from './autentique-events-section'
import { FailuresSection } from './failures-section'
import { PERIOD_LABELS, updatedLabel } from './labels'
import { AttentionList, JobsBlock, NumbersBlock, OverviewSkeleton } from './overview-blocks'

/**
 * Monitoramento (docs/areas/administracao.md, "Monitoramento"; RN-ADM-94 a 99, T61). Arquétipo: visão do
 * dia, só de root, sem depender de clínica ativa. O que a Westhetics faz sozinha por todas as clínicas, no
 * lugar das telas "Monitoramento" e "Autentique" do legado: o que pede atenção primeiro, as rotinas de cada
 * minuto, os números do período, as falhas e os avisos da Autentique. Só leitura (P-ADM-100); tudo se pede
 * de novo a cada minuto. Período, área e páginas ficam no endereço.
 */

const SUBTITLE =
  'O que a Westhetics faz sozinha por todas as clínicas: as rotinas de cada minuto, os envios, a Autentique, o PDF, o e-mail e os webhooks das integrações. Atualiza a cada minuto.'

/** O relógio da tela: "atualizado há 20 segundos" e "última rodada há 40 segundos" andam sozinhos. */
function useNow(intervalMs = 10_000) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs)
    return () => window.clearInterval(id)
  }, [intervalMs])
  return now
}

function pageOf(value: string | null) {
  return Math.max(1, Number(value) || 1)
}

export function OperationsPage() {
  const [params, setParams] = useSearchParams()
  const periodParam = params.get('period') as OperationsPeriod | null
  const period: OperationsPeriod =
    periodParam && OPERATIONS_PERIODS.includes(periodParam) ? periodParam : 'day'
  const areaParam = params.get('area') as OperationAreaFilter | null
  const area: OperationAreaFilter =
    areaParam && OPERATION_AREA_FILTERS.includes(areaParam) ? areaParam : 'all'
  const failuresPage = pageOf(params.get('page'))
  const eventsPage = pageOf(params.get('eventsPage'))

  const now = useNow()
  const overview = useOperationsOverview(period)

  function update(changes: Record<string, string | null>) {
    const next = new URLSearchParams(params)
    for (const [key, value] of Object.entries(changes)) {
      if (value === null) next.delete(key)
      else next.set(key, value)
    }
    setParams(next, { replace: true })
  }

  function changePeriod(value: OperationsPeriod) {
    update({ period: value === 'day' ? null : value, page: null })
  }

  function changeArea(value: OperationAreaFilter) {
    update({ area: value === 'all' ? null : value, page: null })
  }

  function seeFailures(value: OperationArea) {
    changeArea(value)
    document.getElementById('falhas')?.scrollIntoView?.({ block: 'start' })
  }

  const data = overview.data
  const quiet = data && data.attention.length === 0

  return (
    <>
      <PageHeader crumbs={[{ label: 'Westhetics' }, { label: 'Monitoramento' }]} />
      <Page wide>
        <PageTitle
          title="Monitoramento"
          subtitle={SUBTITLE}
          meta={
            data ? (
              <>
                {quiet ? <span>Nada pede atenção agora.</span> : null}
                {quiet ? ' ' : null}
                <span>{updatedLabel(data.generatedAt, now)}</span>
              </>
            ) : undefined
          }
          trailing={
            <FilterSegments
              label="Período"
              value={period}
              onChange={changePeriod}
              options={OPERATIONS_PERIODS.map((value) => ({
                value,
                label: PERIOD_LABELS[value],
              }))}
            />
          }
        />

        <div className="mt-6 flex flex-col gap-8">
          {overview.isError && !data ? (
            <LoadError
              title="Não conseguimos carregar o monitoramento"
              error={overview.error}
              onRetry={() => void overview.refetch()}
            />
          ) : !data ? (
            <OverviewSkeleton />
          ) : (
            <>
              <AttentionList items={data.attention} onSeeFailures={seeFailures} />
              <JobsBlock jobs={data.jobs} now={now} />
              <NumbersBlock overview={data} />
            </>
          )}

          <FailuresSection
            period={period}
            area={area}
            page={failuresPage}
            now={now}
            onAreaChange={changeArea}
            onPageChange={(next) => update({ page: next > 1 ? String(next) : null })}
            onSeeWeek={() => changePeriod('week')}
          />

          <AutentiqueEventsSection
            page={eventsPage}
            now={now}
            onPageChange={(next) => update({ eventsPage: next > 1 ? String(next) : null })}
          />
        </div>
      </Page>
    </>
  )
}
